/* eslint-disable max-lines */
import {
  PaymentMethodResult,
  PaymentRequestPaymentMethodEvent,
  Stripe,
  StripeCardNumberElement,
  StripeError,
} from '@stripe/stripe-js'
import i18n from 'i18next'
import { stripePaymentApi, userApi } from '~/shared/api'
import { CENTS_IN_DOLLAR } from '~/shared/constants/commonConstants'
import {
  DEFAULT_CARDHOLDER_NAME,
  PaymentMethod,
} from '~/shared/constants/paymentConstants'
import { checkIsRetryAllowed } from '~/shared/helpers/checkIsRetryAllowed'
import { getPurchaseFailedEventParams } from '~/shared/helpers/getPurchaseFailedEventParams'
import { getPurchaseStartedEventParams } from '~/shared/helpers/getPurchaseStartedEventParams'
import { getPurchaseSuccessEventParams } from '~/shared/helpers/getPurchaseSuccessEventParams'
import { getRedirectUrl } from '~/shared/helpers/getRedirectUrl'
import { getSubscriptionProductId } from '~/shared/helpers/getSubscriptionProductId'
import { logFailedPayment } from '~/shared/helpers/logFailedPayment'
import { logGeneralAddToCartEvents } from '~/shared/helpers/logGeneralAddToCartEvents'
import { logSuccessfulPayment } from '~/shared/helpers/logSuccessfulPayment'
import { eventLogger } from '~/shared/services/eventLogger'
import { tiktokPixelService } from '~/shared/services/tiktokPixelService'
import {
  setErrorAction,
  startFetching,
  stopFetching,
} from '~/shared/store/common/actions'
import { selectCohortID } from '~/shared/store/common/selects'
import { setSubscriptionIdAction } from '~/shared/store/subscription/actions/syncActions'
import {
  selectPurchasedSubscriptionID,
  selectSelectedSubscription,
  selectSelectedSubscriptionCurrency,
  selectSelectedSubscriptionFullPrice,
  selectSelectedSubscriptionLookupKey,
  selectSelectedSubscriptionPlanID,
  selectSelectedSubscriptionTrialDays,
  selectSelectedSubscriptionTrialLookupKey,
  selectSelectedSubscriptionTrialPrice,
} from '~/shared/store/subscription/selects'
import { IAppState, TAppDispatchThunk } from '~/shared/store/types'
import { sendUserConfigAction } from '~/shared/store/user/actions/asyncActions'
import {
  selectUserPaymentSystem,
  selectUserUUID,
} from '~/shared/store/user/selects'

import { CHECK_3D_SECURE, PURCHASE } from '../actionTypes'
import {
  selectIsForce3DS,
  selectStripeAccountName,
  selectStripeClientSecret,
} from '../selects'
import {
  reset3DSecureIframeUrlAction,
  set3DSecureIframeUrlAction,
  setIsSubscriptionPurchaseInitiated,
  setStripeClientSecretAction,
} from './syncActions'

const getErrorActionPayload = ({ type, message }: StripeError): string =>
  message || type

export const purchaseAction =
  ({
    stripe,
    paymentPageId,
    card,
    name,
    createPaymentResFromDigitalWallet,
  }: {
    stripe: Stripe
    paymentPageId: string
    card?: StripeCardNumberElement
    name?: string
    createPaymentResFromDigitalWallet?: PaymentRequestPaymentMethodEvent
  }): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const selectedSubscriptionPlanID = selectSelectedSubscriptionPlanID(state)
    const priceId = selectSelectedSubscriptionLookupKey(state)
    const trialPriceId = selectSelectedSubscriptionTrialLookupKey(state)
    const uuid = selectUserUUID(state)
    const currency = selectSelectedSubscriptionCurrency(state)
    const trialPrice = selectSelectedSubscriptionTrialPrice(state)
    const currentPrice = selectSelectedSubscriptionFullPrice(state)
    const trialPeriodDays = selectSelectedSubscriptionTrialDays(state)
    const stripeAccountName = selectStripeAccountName(state)
    const cohortID = selectCohortID(state)
    const selectedSubscription = selectSelectedSubscription(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isForce3DS = selectIsForce3DS(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    const commonPurchaseStartedParams = getPurchaseStartedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    dispatch(startFetching(PURCHASE))
    dispatch(setIsSubscriptionPurchaseInitiated(true))

    const paymentMethod =
      createPaymentResFromDigitalWallet?.paymentMethod?.card?.wallet?.type

    const isDigitalWallet =
      paymentMethod === PaymentMethod.APPLE_PAY ||
      paymentMethod === PaymentMethod.GOOGLE_PAY

    eventLogger.logPurchaseStarted({
      ...commonPurchaseStartedParams,
      paymentMethod,
      paymentSystem,
      isForce3DS,
    })

    if (isDigitalWallet) {
      eventLogger.logPurchaseStartedPopup({
        ...commonPurchaseStartedParams,
        paymentMethod,
        paymentSystem,
        isForce3DS,
      })
    }

    logGeneralAddToCartEvents({
      uuid,
      selectedSubscription,
      currency: currency.toUpperCase(),
      price: trialPrice || currentPrice,
    })

    if (selectedSubscription) {
      tiktokPixelService.trackInitiateCheckout({
        value: trialPrice || currentPrice,
        currency,
        contentId: getSubscriptionProductId(selectedSubscription),
        eventId: uuid,
      })
    }

    try {
      const createPaymentResponse =
        card && !createPaymentResFromDigitalWallet
          ? await stripe.createPaymentMethod({
              type: 'card',
              card,
              billing_details: { name: name || DEFAULT_CARDHOLDER_NAME },
            })
          : (createPaymentResFromDigitalWallet as PaymentMethodResult)

      if (
        !createPaymentResponse?.paymentMethod &&
        createPaymentResponse?.error
      ) {
        const {
          error: { type, message },
        } = createPaymentResponse

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          error: createPaymentResponse.error,
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })
        dispatch(setErrorAction(message || type))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const isFreeTrial = !!trialPeriodDays && !trialPriceId

      const createSubscriptionResponse =
        await stripePaymentApi.createSubscription({
          uuid,
          planId: selectedSubscriptionPlanID,
          trialPeriodDays,
          cohort: cohortID,
        })

      if (
        !createSubscriptionResponse.success ||
        !createSubscriptionResponse.data
      ) {
        // It's a hack for fixing behavior after changing response
        // from WS BE after getting payment_method
        if (
          createSubscriptionResponse.data?.error?.startsWith(
            'creating new customer',
          )
        ) {
          let error = { type: createSubscriptionResponse.data?.error }

          try {
            error = JSON.parse(
              createSubscriptionResponse.data.error.split(
                'creating new customer: ',
              )[1],
            )
          } finally {
            logFailedPayment({
              ...commonPurchaseFailedParams,
              paymentResponse: error,
              paymentMethod,
              paymentSystem,
              isForce3DS,
            })
            dispatch(
              setErrorAction({ type: createSubscriptionResponse.data?.error }),
            )
            dispatch(stopFetching(PURCHASE))
            createPaymentResFromDigitalWallet?.complete('fail')
          }
          return
        }

        if (isFreeTrial) {
          logFailedPayment({
            ...commonPurchaseFailedParams,
            paymentResponse: { type: createSubscriptionResponse.data?.error },
            paymentMethod,
            paymentSystem,
            isForce3DS,
          })
          dispatch(
            setErrorAction({ type: createSubscriptionResponse.data?.error }),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        switch (createSubscriptionResponse.status) {
          case 409:
            dispatch(setErrorAction(i18n.t('payment.haveSubscription')))
            break
          default:
            dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: { type: createSubscriptionResponse.data?.error },
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      if (isFreeTrial) {
        const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

        logSuccessfulPayment({
          ...commonPurchaseSuccessParams,
          predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
          productId: createSubscriptionResponse.data.purchase.product_id,
          trialPeriodDays:
            createSubscriptionResponse.data.purchase.trial_period_days,
          subscriptionId:
            createSubscriptionResponse.data.purchase.subscription_id,
          paymentMethod,
          paymentSystem,
          stripeAccountName,
          isForce3DS,
        })
        dispatch(
          sendUserConfigAction({
            payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
            subscription_price: currentPrice,
            trial_period: trialPeriodDays,
          }),
        )

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('success')
        return
      }

      dispatch(
        setStripeClientSecretAction(
          createSubscriptionResponse.data.purchase.client_secret,
        ),
      )
      dispatch(
        setSubscriptionIdAction(
          createSubscriptionResponse.data.purchase.subscription_id,
        ),
      )

      const cardPaymentResponseFirst = await stripe.confirmCardPayment(
        createSubscriptionResponse.data.purchase.client_secret,
        {
          payment_method: createPaymentResponse.paymentMethod.id,
          save_payment_method: true,
          return_url: getRedirectUrl(paymentPageId),
        },
      )

      if (
        !cardPaymentResponseFirst?.paymentIntent &&
        cardPaymentResponseFirst?.error
      ) {
        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseFirst.error,
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseFirst)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseFirst = await stripePaymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFirst.error as StripeError,
        })

        if (!retryResponseFirst.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseFirst.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setStripeClientSecretAction(
            createSubscriptionResponse.data.purchase.client_secret,
          ),
        )
        dispatch(
          setSubscriptionIdAction(
            createSubscriptionResponse.data.purchase.subscription_id,
          ),
        )

        const cardPaymentResponseSecond = await stripe.confirmCardPayment(
          retryResponseFirst.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseSecond?.paymentIntent &&
          !cardPaymentResponseSecond?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPrice:
              cardPaymentResponseSecond.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPeriodDays:
              retryResponseFirst.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseFirst.data.subscription.subscription_id,
            discountApplied:
              retryResponseFirst.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem,
            stripeAccountName,
            isForce3DS,
          })

          dispatch(
            sendUserConfigAction({
              payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
              subscription_price: currentPrice,
              trial_period: trialPeriodDays,
            }),
          )

          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseSecond.error,
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseSecond)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseSecond = await stripePaymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseSecond.error as StripeError,
        })

        if (!retryResponseSecond.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseSecond.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setStripeClientSecretAction(
            createSubscriptionResponse.data.purchase.client_secret,
          ),
        )
        dispatch(
          setSubscriptionIdAction(
            createSubscriptionResponse.data.purchase.subscription_id,
          ),
        )

        const cardPaymentResponseThird = await stripe.confirmCardPayment(
          retryResponseSecond.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseThird?.paymentIntent &&
          !cardPaymentResponseThird?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPrice:
              cardPaymentResponseThird.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPeriodDays:
              retryResponseSecond.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseSecond.data.subscription.subscription_id,
            discountApplied:
              retryResponseSecond.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem,
            stripeAccountName,
            isForce3DS,
          })

          dispatch(
            sendUserConfigAction({
              payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
              subscription_price: currentPrice,
              trial_period: trialPeriodDays,
            }),
          )

          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseThird.error,
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })

        if (!checkIsRetryAllowed(cardPaymentResponseThird)) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        const retryResponseThird = await stripePaymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseThird.error as StripeError,
        })

        if (!retryResponseThird.data.should_retry) {
          dispatch(
            setErrorAction(
              getErrorActionPayload(cardPaymentResponseThird.error),
            ),
          )
          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('fail')
          return
        }

        dispatch(
          setStripeClientSecretAction(
            createSubscriptionResponse.data.purchase.client_secret,
          ),
        )
        dispatch(
          setSubscriptionIdAction(
            createSubscriptionResponse.data.purchase.subscription_id,
          ),
        )

        const cardPaymentResponseFourth = await stripe.confirmCardPayment(
          retryResponseThird.data.subscription.client_secret,
          {
            payment_method: createPaymentResponse.paymentMethod.id,
            save_payment_method: true,
            return_url: getRedirectUrl(paymentPageId),
          },
        )

        if (
          cardPaymentResponseFourth?.paymentIntent &&
          !cardPaymentResponseFourth?.error
        ) {
          const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

          logSuccessfulPayment({
            ...commonPurchaseSuccessParams,
            predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
            productId: createSubscriptionResponse.data.purchase.product_id,
            trialPrice:
              cardPaymentResponseFourth.paymentIntent.amount / CENTS_IN_DOLLAR,
            trialPeriodDays:
              retryResponseThird.data.subscription.trial_period_days,
            subscriptionId:
              retryResponseThird.data.subscription.subscription_id,
            discountApplied:
              retryResponseThird.data.subscription.discount_applied,
            paymentMethod,
            paymentSystem,
            stripeAccountName,
            isForce3DS,
          })

          dispatch(
            sendUserConfigAction({
              payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
              subscription_price: currentPrice,
              trial_period: trialPeriodDays,
            }),
          )

          dispatch(stopFetching(PURCHASE))
          createPaymentResFromDigitalWallet?.complete('success')
          return
        }

        logFailedPayment({
          ...commonPurchaseFailedParams,
          paymentResponse: cardPaymentResponseFourth.error,
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })

        dispatch(
          setErrorAction(
            getErrorActionPayload(cardPaymentResponseFourth.error),
          ),
        )

        // Needed for reset invoice on BE
        await stripePaymentApi.retryPayment({
          uuid,
          stripeError: cardPaymentResponseFourth.error as StripeError,
        })

        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const { paymentIntent } = cardPaymentResponseFirst

      if (paymentIntent.status === 'requires_payment_method') {
        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          error: {
            type: 'requires_payment_method',
          },
          paymentMethod,
          paymentSystem,
          isForce3DS,
        })
        dispatch(setErrorAction('Payment failed'))
        dispatch(stopFetching(PURCHASE))
        createPaymentResFromDigitalWallet?.complete('fail')
        return
      }

      const threeDSesureURL = paymentIntent.next_action?.redirect_to_url?.url

      if (paymentIntent.status === 'requires_action' && threeDSesureURL) {
        dispatch(set3DSecureIframeUrlAction(threeDSesureURL))
        dispatch(stopFetching(PURCHASE))
        return
      }
      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        productId: createSubscriptionResponse.data.purchase.product_id,
        trialPeriodDays:
          createSubscriptionResponse.data.purchase.trial_period_days,
        subscriptionId:
          createSubscriptionResponse.data.purchase.subscription_id,
        paymentMethod,
        paymentSystem,
        stripeAccountName,
        isForce3DS,
      })

      dispatch(
        sendUserConfigAction({
          payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
          subscription_price: currentPrice,
          trial_period: trialPeriodDays,
        }),
      )

      dispatch(stopFetching(PURCHASE))
      createPaymentResFromDigitalWallet?.complete('success')
    } catch (error: any) {
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(PURCHASE))
      createPaymentResFromDigitalWallet?.complete('fail')
    }
  }

export const check3DSecure =
  (stripe: Stripe): any =>
  async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ): Promise<void> => {
    const state = getState()
    const priceId = selectSelectedSubscriptionLookupKey(state)
    const currentPrice = selectSelectedSubscriptionFullPrice(state)
    const clientSecret = selectStripeClientSecret(state)
    const trialPeriodDays = selectSelectedSubscriptionTrialDays(state)
    const subscriptionId = selectPurchasedSubscriptionID(state)
    const stripeAccountName = selectStripeAccountName(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isForce3DS = selectIsForce3DS(state)
    const uuid = selectUserUUID(state)

    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)
    const commonPurchaseSuccessParams = getPurchaseSuccessEventParams(state)

    if (!priceId || !currentPrice) {
      console.error('Error: no subscriptions plan selected')
      return
    }

    if (!clientSecret) {
      console.error('Error: client secret is needed')
      return
    }

    dispatch(startFetching(CHECK_3D_SECURE))

    const response = await stripe.retrievePaymentIntent(clientSecret)

    if (response.paymentIntent?.status === 'succeeded') {
      const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

      logSuccessfulPayment({
        ...commonPurchaseSuccessParams,
        predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
        trialPeriodDays,
        subscriptionId,
        stripeAccountName,
        paymentSystem,
        isForce3DS,
      })

      dispatch(reset3DSecureIframeUrlAction())
      return
    }

    if (response.paymentIntent?.status === 'requires_payment_method') {
      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        error: {
          type: 'requires_payment_method',
        },
        paymentSystem,
        isForce3DS,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction('Payment failed'))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    if (response.error) {
      const {
        error: { type, message },
      } = response

      eventLogger.logPurchaseFailed({
        ...commonPurchaseFailedParams,
        error: response.error,
        paymentSystem,
        isForce3DS,
      })
      dispatch(reset3DSecureIframeUrlAction())
      dispatch(setErrorAction(message || type))
      dispatch(stopFetching(CHECK_3D_SECURE))
      return
    }

    dispatch(reset3DSecureIframeUrlAction())
    dispatch(setErrorAction('Error: unhandled checking 3D Secure error'))
    dispatch(stopFetching(CHECK_3D_SECURE))
  }
